// ABOUTME: Pure geometry for mapping Figma-native coordinates onto an object-fit video (SPEC-26 Phase 4).
// ABOUTME: No React, no DOM — kept separate from useVideoBox so the crop maths is unit-testable on its own.

/**
 * Where an `object-fit: cover`/`contain` video's content actually sits inside its container.
 *
 * `cover` scales by the LARGER of the two ratios so the frame always fills the box, which means
 * the overflowing axis is cropped equally on both sides (hence the negative offset). Point-tag
 * anchors are authored against the uncropped Figma frame, so without this every tag would drift
 * as soon as the viewport stopped being exactly the authored aspect ratio.
 *
 * `contain` scales by the SMALLER ratio instead: nothing is cropped and the short axis carries a
 * letterbox band, so the offset on that axis is POSITIVE. Anchors must follow the band or every
 * tag sits too high/left by exactly half of it — the mirror of the crop case. Introduced when the
 * mobile reel pair moved to `contain` (the mobile master is a portrait 1080x1920 cut, so `cover`
 * on a 390x844 handset was hiding 17.9% of its width and clipping the outer watches in the
 * lineup); `cover` stays the default so every desktop call site is untouched.
 *
 * @param {'cover'|'contain'} [fit='cover']
 * @returns {{scale:number, offsetX:number, offsetY:number, renderedW:number, renderedH:number,
 *            containerW:number, containerH:number}|null} null when any dimension is missing/zero.
 */
export function computeVideoBox(containerW, containerH, nativeW, nativeH, fit = 'cover') {
  if (!containerW || !containerH || !nativeW || !nativeH) return null;

  const ratioW = containerW / nativeW;
  const ratioH = containerH / nativeH;
  const scale = fit === 'contain' ? Math.min(ratioW, ratioH) : Math.max(ratioW, ratioH);
  const renderedW = nativeW * scale;
  const renderedH = nativeH * scale;

  return {
    scale,
    // Negative on the cropped axis under `cover` — that's the point: it shifts anchors left/up by
    // exactly the amount of frame the browser has hidden off-edge. Positive on the letterboxed
    // axis under `contain`, shifting them right/down by half the band. Same expression either way.
    offsetX: (containerW - renderedW) / 2,
    offsetY: (containerH - renderedH) / 2,
    renderedW,
    renderedH,
    containerW,
    containerH,
  };
}

/**
 * Map a point in native Figma-frame pixels to a container-relative CSS percentage position.
 * Returned unclamped and exact — callers decide what to do with points that fall outside the
 * container (see `clampPercent`), so this stays a pure coordinate transform.
 *
 * @returns {{left:string, top:string, leftPct:number, topPct:number, visible:boolean}|null}
 */
export function mapNativePoint(box, x, y) {
  if (!box) return null;

  const px = box.offsetX + x * box.scale;
  const py = box.offsetY + y * box.scale;
  const leftPct = (px / box.containerW) * 100;
  const topPct = (py / box.containerH) * 100;

  return {
    leftPct,
    topPct,
    left: `${leftPct}%`,
    top: `${topPct}%`,
    // False when the crop has pushed this anchor off-screen entirely.
    visible: leftPct >= 0 && leftPct <= 100 && topPct >= 0 && topPct <= 100,
  };
}

/**
 * Keep a percentage inside a safe on-screen band.
 *
 * This is for the last few percent only — an anchor that is still on screen but close enough to an
 * edge that the tag would hang off it. It is NOT the answer to an anchor the crop has removed
 * entirely: clamping one of those would slide the tag tens of percent across the picture and leave
 * it pointing at whatever happens to be under it, which is exactly the failure
 * `chapterTagAnchors.js` refuses to accept. Callers must drop those first — check
 * `mapNativePoint(...).visible`, as `PointTagLayer` does.
 */
export function clampPercent(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
